/**
 * Translation keys (register in backend / CMS):
 * - sync.tab.exportqueue, sync.exportqueue, sync.exportqueue.description, sync.exportqueue.empty
 * - sync.exportqueue.col.targetsystem, .registered, .exported, .status
 * - sync.exportqueue.field.id, .action, .lasterror, .target, .targetvalue
 * - sync.exportqueue.delete.confirm
 */
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {Permission} from "../../../constants";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {createErrorMessage} from "../../../util/misc";
import {global, lm} from "../../../appContext";
import pubsub from "../../../pubsub";
import {IMessageData, ISyncExportQueueItem} from "../../../index";
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

interface IExportQueueStatusObj {
    text: string
    icon: string
    status: string
}

interface IExportQueueTabParent {
    canDeleteQueueItem(): boolean
    deleteQueueItem(id: number): Promise<unknown>
    loadQueue(): Promise<unknown>
    readonly messageData: KnockoutObservable<IMessageData | null>
}

class ExportQueueRowVm {
    readonly isExpanded = ko.observable(false);
    readonly isDeleting = ko.observable(false);

    constructor(
        readonly item: ISyncExportQueueItem,
        private readonly parentView: IExportQueueTabParent,
    ) {
    }

    getRowBindings() {
        return {
            attr: {'aria-expanded': this.isExpanded},
        };
    }

    doExpandToggle = () => this.isExpanded(!this.isExpanded());

    canShowDeleteButton() {
        return this.parentView.canDeleteQueueItem() && !this.item.exported;
    }

    getStatusObj(): IExportQueueStatusObj {
        const i = this.item;
        if (i.lasterror) {
            return {text: 'synccycle.step.status.error', icon: 'fa-ban', status: 'halted'};
        }
        if (i.exported) {
            return {text: 'synccycle.step.status.executed', icon: 'fa-check-circle', status: 'executed'};
        }
        return {text: 'synccycle.step.status.waiting', icon: 'fa-clock', status: 'waiting'};
    }

    expandedIdText() {
        const id = this.item.id;
        return id == null ? '' : String(id);
    }

    expandedActionText() {
        return ExportQueueRowVm.dashIfEmptyString(this.item.action);
    }

    expandedLasterrorText() {
        return ExportQueueRowVm.dashIfEmptyString(this.item.lasterror);
    }

    expandedTargetText() {
        return ExportQueueRowVm.dashIfEmptyString(this.item.target);
    }

    expandedTargetvalueText() {
        return ExportQueueRowVm.dashIfEmptyString(this.item.targetvalue);
    }

    private static dashIfEmptyString(value: string | null | undefined) {
        if (value == null) {
            return '';
        }
        const t = String(value).trim();
        return t;
    }

    onDeleteClick = () => {
        if (!this.canShowDeleteButton()) {
            return;
        }
        if (!window.confirm(lm.t('sync.exportqueue.delete.confirm') ?? '')) {
            return;
        }
        this.isDeleting(true);
        this.parentView.deleteQueueItem(this.item.id)
            .then(() => this.parentView.loadQueue())
            .catch((reason: string) => {
                this.parentView.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isDeleting(false);
            });
    };
}

export class viewModel implements IExportQueueTabParent {
    readonly items = ko.observableArray<ExportQueueRowVm>([]);
    readonly messageData = ko.observable<IMessageData>(null);
    readonly isLoading = ko.observable(true);

    private readonly reqGet = new PromiseRequest('getsyncexportqueue');
    private readonly reqDelete = new PromiseRequest('deleteexportqueue');

    constructor() {
        pubsub.on(this, 'company.sync.cycle.status.updated', () => {
            this.loadQueue();
        });
        this.loadQueue();
    }

    canDeleteQueueItem() {
        return global.permissionValidator.role() >= Permission.SuperManager;
    }

    loadQueue() {
        this.isLoading(true);
        this.items([]);
        this.messageData({message: PLEASE_WAIT_MESSAGE});
        this.reqGet.abort();
        return this.reqGet
            .request()
            .then((data: ISyncExportQueueItem[]) => {
                this.items(data.map((item) => new ExportQueueRowVm(item, this)));
                this.messageData(null);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            })
            .finally(() => {
                this.isLoading(false);
            });
    }

    deleteQueueItem(id: number) {
        this.reqDelete.abort();
        return this.reqDelete.request({id});
    }

    dispose() {
        pubsub.off(this);
        this.reqGet.abort();
        this.reqDelete.abort();
    }
}
